<template>
<AbstractCard :product="product"
              :category="product"
              :promotion-id="promotionId"
              :reward-id="rewardId"
              v-bind="$attrs"
              v-on="$listeners"
              :no-description="false"
              :no-variant-labels="withoutVariantOptionLabels"
              class="detail"
              :style="{'--image-aspect-ratio': productsSettings.card_image_aspect_ratio, '--image-object-fit': productsSettings.card_image_object_fit}"
>
  <template slot="top">
    <div
      class="gallery swiper-container"
      v-swiper="!noPagination ? swiperParams : null"
    >
      <div class="swiper-wrapper">
        <figure
          v-for="image of media"
          :key="image.id"
          class="swiper-slide image"
        >
          <responsive-image :media="image" :sizes="['large', 'medium']" :withoutPlaceholder="true" />
        </figure>
        <figure
          v-if="noMedia"
          class="swiper-slide image image--placeholder"
        >
          <img :src="photoPlaceholder">
        </figure>
      </div>

      <template v-if="!noPagination">
        <div class="swiper-pagination"/>

        <div class="swiper-button-prev" ref="swiperButtonPrev">
          <i class="fa fa-chevron-left"/>
        </div>
        <div class="swiper-button-next" ref="swiperButtonNext">
          <i class="fa fa-chevron-right"/>
        </div>
      </template>
    </div>
    <button class="close-button" @click="$emit('close')">
      <img :src="require('@/assets/images/icons/icon-close-black.svg')" alt="Закрыть">
    </button>
  </template>
  <template #bottom="{ instance }">
    <section class="sharing"></section>
    <section v-if="instance.variantWeight || product.features.length > 0" class="features">
      <h4 class="header">Характеристики</h4>
      <table>
        <tbody>
          <tr v-if="instance.variantWeight">
            <td>Вес</td>
            <td>{{ instance.variantWeight }}</td>
          </tr>
          <tr
            v-for="f in product.features"
            :key="f.id"
          >
            <td>{{ f.feature.name }}</td>
            <td>{{ f.value.name }}</td>
          </tr>
        </tbody>
      </table>
    </section>
  </template>
</AbstractCard>
</template>

<script>
import { mapGetters } from 'vuex'
import AbstractCard from './partials/AbstractCard'
import ResponsiveImage from '@/components/images/ResponsiveImage'

export default {
  directives: {
    swiper: {
      async bind(el, binding, vnode) {
        if (!binding.value) {
          return;
        }

        el.classList.add('swiper-not-ready');

        const { Swiper, modules } = (await import('./treeshakenSwiper'));
        Swiper.use(modules);

        if (el.swiperUnbound) return;
        el.classList.remove('swiper-not-ready');

        el.swiper = new Swiper(el, binding.value);
      },
      componentUpdated(el) {
        // TODO am I to understand that this is called on each change, not only on those that affect this element?
        // Then only update in the second case. Perhaps, using `binding.oldValue`?
        el.swiper?.update();
      },
      unbind(el) {
        el.swiper?.destroy();
        el.swiperUnbound = true;
      },
    },
  },
  props: {
    product: {
      required: true,
      type: Object,
    },

    category: {
      required: false,
      type: Object,
      default: () => ({}),
    },

    promotionId: {
      required: false,
      type: String,
    },

    rewardId: {
      required: false,
      type: String,
    },
  },

  components: {
    AbstractCard,
    ResponsiveImage,
  },

  computed: {
    ...mapGetters({
      productsSettings: 'settings/products',
      photoPlaceholder: 'ui/photoPlaceholder',
    }),

    media() {
      if (!(this.product.media?.length > 0)) {
        return [];
      }
      return this.product.media;
    },

    noMedia() {
      return this.media.length === 0;
    },

    noPagination() {
      return this.media.length <= 1;
    },

    swiperParams() {
      return {
        loop: true,
        spaceBetween: 25, // WET. See @gallery-padding-y in styles
        navigation: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        },
        pagination: {
          el: '.swiper-pagination',
        },
      };
    },

    withoutVariantOptionLabels() {
      switch (this.productsSettings.display_variant_option_label) {
        case 'never':
          return true;
        case 'always':
        case 'product_page':
          return false;
      }

      return true;
    },
  }
}
</script>

<style lang="less">
@import "~@/assets/less/components/utilities";

.product-card.detail {
  @apply px-5 relative overflow-hidden; // So `DistortedButton.vue` does not create horizontal scroll.
  .close-button {
    @apply absolute;
    // So swiper doesn't cover it. I think it would be better to set `z-index` of Swiper element to `auto`, but I'm
    // afraid te break it.
    z-index: 2;
    top: theme('padding.3');
    right: theme('padding.3');
  }
  @gallery-padding-y: theme('padding.5'); // WET. See spaceBetween in JS
  .gallery {
    @apply mb-4;
    @apply -mx-5;

    .image {
      .aspect-ratio-image(var(--image-aspect-ratio));

      img {
        object-fit: var(--image-object-fit);
      }

      &--placeholder {
        img {
          @apply object-cover;
        }
      }
    }
  }

  --swiper-theme-color: theme('colors.primary');
  .swiper-container {
    &.swiper-not-ready {
      @apply overflow-x-auto;
      .swiper-slide {
        margin-right: @gallery-padding-y;
      }
    }
  }
  .swiper-pagination-bullets {
    bottom: calc(@gallery-padding-y + theme('margin.2'));
    line-height: 0;
  }
  .swiper-pagination-bullet {
    border-color: rgba(0, 0, 0, 0.7);
    border-width: 0.2px;
    box-sizing: content-box;
    // `!important` because the rule that we're overriding is too specific.
    margin: 0 calc(theme('margin.3') / 2) !important;
  }
  .swiper-pagination-bullet:not(.swiper-pagination-bullet-active) {
    @apply bg-white;
    opacity: 1;
  }
  @navigation-button-margin: theme('margin.3');
  @navigation-button-size: theme('width.10');
  .swiper-button-prev,
  .swiper-button-next {
    @apply flex rounded-full border-2 border-dark text-dark text-lg;
    width: @navigation-button-size;
    height: @navigation-button-size;
    background-color: rgba(255, 255, 255, 0.5);
    right: @gallery-padding-y;
    margin: @navigation-button-margin;
    left: unset;
    top: unset;
    .fa {
      font-weight: inherit;
    }
    &:after {
      @apply hidden;
    }
  }
  .swiper-button-next {
    bottom: @gallery-padding-y;
  }
  .swiper-button-prev {
    bottom: calc(@gallery-padding-y + @navigation-button-size + @navigation-button-margin);
  }


  .product-title {
    @apply font-normal text-2xl my-2 opacity-80;
  }
  .product-short-description {
    @apply font-light text-base my-2 opacity-60;
  }
  .product-variants-wrapper {
    @apply my-3;
    .product-variants-list {
      @apply flex flex-wrap;

      @apply -mr-5 -mb-4;
      > * {
        @apply mr-5 mb-4;
      }
    }
  }

  .product-variant-selector {
    flex-basis: 8rem;
    label {
      @apply mb-2 text-sm font-light opacity-80;
    }
    .product-variant-select {
      @apply text-sm;
      &.v-select {
        .vs__dropdown-toggle {
          @apply p-3 rounded border border-primary-lightest;
          &:hover {
            @apply border-primary;
          }
        }
        .vs__dropdown-menu {
          max-height: 120px;
          @apply border-r border-b border-l border-primary rounded-b overflow-hidden overflow-y-scroll;
          .vs__dropdown-option {
            @apply p-2;
            &--highlight {
              @apply bg-primary-lighter;
            }
            &--selected {
              @apply text-white bg-primary;
            }
          }
        }
        &.vs--open {
          .vs__dropdown-toggle {
          @apply border-primary rounded-b-none;
          }
        }
      }
    }
  }

  .price-and-buttons {
    &:not(.has-discounted-price) {
      @apply my-3;
    }

    &.has-discounted-price {
      @apply mt-0 mb-3;
    }

    .product-price {
      @apply font-medium;
    }
    .quantity-controls {
      @apply w-32 h-10;
    }
  }
  .sharing {
    @apply -mx-5;
    @apply border-t border-light-dark my-5;
  }
  .features {
    @apply my-5;
    .header {
      @apply font-normal text-lg text-dark-dark my-3;
    }
    table {
      @apply w-full table-fixed;
      tr {
        &:nth-child(odd) {
          @apply bg-light;
        }
        td {
          @apply w-1/2 px-2 py-2 font-light text-sm text-light-darkest;
        }
      }
    }
  }
}

@screen md {
  .product-card.detail {
    .close-button {
      top: theme('padding.8');
      right: theme('padding.8');
    }
  }
}
</style>
